import { Skeleton } from '@mantine/core'
import { ResponsiveBar } from '@nivo/bar'
import type { BarDatum, BarLayer } from '@nivo/bar'

import type { CompletionsByChannelRow } from '../mockData'

import {
  chartTheme,
  formatMonth,
  STACKED_BAR_KEYS,
  STACKED_BAR_COLORS,
  COMPLETION_METHOD_LABELS,
} from './chartTheme'
import { TotalLabelLayer } from './TotalLabelLayer'

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

interface TooltipProps {
  id: string | number
  value: number
  indexValue: string | number
  data: BarDatum
  color: string
}

const BarTooltip = ({ id, indexValue, data }: TooltipProps) => {
  const total = STACKED_BAR_KEYS.reduce(
    (sum, key) => sum + ((data[key] as number) ?? 0),
    0
  )
  return (
    <div className="rounded-lg bg-white px-3 py-2 shadow-md ring-1 ring-gray-200">
      <p className="mb-1.5 text-xs font-medium text-gray-500">
        {formatMonth(String(indexValue))}
      </p>
      <div className="flex flex-col gap-1">
        {STACKED_BAR_KEYS.map((key) => {
          const count = (data[key] as number) ?? 0
          if (count === 0) return null
          return (
            <div
              key={key}
              className={`flex items-center justify-between gap-4 ${
                String(id) === key ? 'font-semibold' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{
                    backgroundColor: STACKED_BAR_COLORS[key] ?? '#94a3b8',
                  }}
                />
                <span className="text-xs text-gray-700">
                  {COMPLETION_METHOD_LABELS[key] ?? key}
                </span>
              </div>
              <span className="text-xs text-gray-700">{count}</span>
            </div>
          )
        })}
      </div>
      <p className="mt-1.5 flex justify-between border-t border-gray-100 pt-1.5 text-xs font-semibold text-gray-700">
        <span>Total</span>
        <span>{total}</span>
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface VoEStackedBarChartProps {
  data: CompletionsByChannelRow[]
  loading?: boolean
}

export const VoEStackedBarChart = ({
  data,
  loading = false,
}: VoEStackedBarChartProps) => {
  if (loading) {
    return <Skeleton height={350} />
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-[350px] items-center justify-center text-sm text-gray-400">
        No data available
      </div>
    )
  }

  return (
    <div className="h-[350px]">
      <ResponsiveBar
        data={data}
        keys={[...STACKED_BAR_KEYS]}
        indexBy="month"
        groupMode="stacked"
        margin={{ top: 30, right: 20, bottom: 80, left: 50 }}
        padding={0.35}
        valueScale={{ type: 'linear' }}
        indexScale={{ type: 'band', round: true }}
        colors={(bar) => STACKED_BAR_COLORS[bar.id] ?? '#94a3b8'}
        enableLabel={false}
        enableGridY={false}
        axisBottom={{
          tickSize: 0,
          tickPadding: 8,
          format: formatMonth,
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 8,
          tickValues: 5,
        }}
        theme={chartTheme}
        tooltip={BarTooltip}
        legendLabel={(datum) =>
          COMPLETION_METHOD_LABELS[String(datum.id)] ?? String(datum.id)
        }
        legends={[
          {
            dataFrom: 'keys',
            anchor: 'bottom',
            direction: 'row',
            translateY: 60,
            itemWidth: 110,
            itemHeight: 20,
            itemTextColor: '#6b7280',
            symbolSize: 12,
            symbolShape: 'circle',
          },
        ]}
        layers={[
          'grid',
          'axes',
          'bars',
          TotalLabelLayer as BarLayer<BarDatum>,
          'markers',
          'legends',
          'annotations',
        ]}
      />
    </div>
  )
}
